import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryKeys';
import { AlertCircle, ArrowRight } from 'lucide-react';
import type { AlertStatus, TeamHealthSnapshot, Product } from '@sprintpulse/shared';
import { METRIC_META } from '@sprintpulse/shared';
import { Link } from 'react-router-dom';
import { usePreferences } from '../hooks/usePreferences';

const STATUS_DOT: Record<AlertStatus, string> = {
  green:   'var(--green)',
  amber:   'var(--amber)',
  red:     'var(--red)',
  unknown: 'var(--text-3)',
};

const STATUS_LABEL: Record<AlertStatus, string> = {
  green: 'On track', amber: 'Watch', red: 'Needs attention', unknown: 'No data',
};

function HealthBar({ alerts }: { alerts: TeamHealthSnapshot['alerts'] }) {
  const counts = { green: 0, amber: 0, red: 0, unknown: 0 };
  alerts.forEach((a) => counts[a.status]++);
  const total = alerts.filter((a) => a.status !== 'unknown').length || 1;

  return (
    <div className="flex h-1 rounded-full overflow-hidden gap-px" style={{ background: 'var(--bg-subtle)' }}>
      {counts.green > 0 && (
        <div style={{ width: `${(counts.green / total) * 100}%`, background: 'var(--green)' }} />
      )}
      {counts.amber > 0 && (
        <div style={{ width: `${(counts.amber / total) * 100}%`, background: 'var(--amber)' }} />
      )}
      {counts.red > 0 && (
        <div style={{ width: `${(counts.red / total) * 100}%`, background: 'var(--red)' }} />
      )}
    </div>
  );
}

function TeamCard({ snapshot }: { snapshot: TeamHealthSnapshot }) {
  const redAlerts = snapshot.alerts.filter((a) => a.status === 'red');
  const amberAlerts = snapshot.alerts.filter((a) => a.status === 'amber');
  const criticalMetrics = [...redAlerts, ...amberAlerts].slice(0, 3);

  return (
    <Link to={`/team/${snapshot.team.id}`}>
      <div
        className="rounded-lg p-5 border transition-all hover:shadow-sm group"
        style={{
          background: 'var(--bg-panel)',
          borderColor: redAlerts.length > 0 ? 'var(--red)' : 'var(--border)',
          borderLeftWidth: redAlerts.length > 0 ? '3px' : '1px',
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
              {snapshot.team.name}
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
              {snapshot.sprint.name}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: STATUS_DOT[snapshot.overallStatus] }}
            />
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>
              {STATUS_LABEL[snapshot.overallStatus]}
            </span>
          </div>
        </div>

        {/* Health bar */}
        <HealthBar alerts={snapshot.alerts} />

        {/* Critical metrics */}
        {criticalMetrics.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {criticalMetrics.map((alert) => (
              <div key={alert.metricName} className="flex items-center justify-between">
                <span className="text-xs" style={{ color: 'var(--text-2)' }}>
                  {METRIC_META[alert.metricName]?.label}
                </span>
                <span
                  className="text-xs font-mono font-medium"
                  style={{ color: alert.status === 'red' ? 'var(--red)' : 'var(--amber)' }}
                >
                  {alert.value !== null ? alert.value.toFixed(1) : '—'}
                  <span className="ml-0.5 font-sans font-normal" style={{ color: 'var(--text-3)' }}>
                    {METRIC_META[alert.metricName]?.unit}
                  </span>
                </span>
              </div>
            ))}
          </div>
        )}

        {/* View link */}
        <div
          className="mt-4 flex items-center gap-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: 'var(--accent)' }}
        >
          View sprint detail <ArrowRight size={11} />
        </div>
      </div>
    </Link>
  );
}

export function DashboardPage(): JSX.Element {
  const { preferences, updatePreferences } = usePreferences();
  const pinnedProductIds = preferences?.pinnedProductIds ?? [];

  // Load products to validate pinned IDs (deleted products should not filter the dashboard)
  const { data: products = [] } = useQuery({ queryKey: queryKeys.products(), queryFn: api.getProducts });
  const validPinnedIds = useMemo(
    () => products.length > 0
      ? pinnedProductIds.filter((id) => products.some((p: Product) => p.id === id))
      : pinnedProductIds,
    [pinnedProductIds, products],
  );

  // Auto-clean stale pinned IDs from preferences
  useEffect(() => {
    if (products.length > 0 && validPinnedIds.length < pinnedProductIds.length) {
      updatePreferences({ pinnedProductIds: validPinnedIds });
    }
  }, [products.length, validPinnedIds.length, pinnedProductIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveIds = validPinnedIds.length > 0 ? validPinnedIds : undefined;
  const { data: snapshots = [], isLoading, error } = useQuery({
    queryKey: [...queryKeys.dashboard(), effectiveIds ?? 'all'],
    queryFn: () => api.getDashboard(effectiveIds),
    refetchInterval: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const greenCount = snapshots.filter((s) => s.overallStatus === 'green').length;
  const amberCount = snapshots.filter((s) => s.overallStatus === 'amber').length;
  const redCount   = snapshots.filter((s) => s.overallStatus === 'red').length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: 'var(--border-mid)', borderTopColor: 'var(--accent)' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 flex items-center gap-2 text-sm" style={{ color: 'var(--red)' }}>
        <AlertCircle size={14} /> Failed to load dashboard
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--text-1)' }}>
          Sprint overview
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-3)' }}>
          Latest closed sprint per team · refreshes every 5 min
        </p>
      </div>

      {/* Summary strip */}
      {snapshots.length > 0 && (
        <div
          className="flex items-center gap-8 mb-8 pb-6 border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          {[
            { label: 'On track', count: greenCount, color: 'var(--green)' },
            { label: 'Watch',    count: amberCount, color: 'var(--amber)' },
            { label: 'Action',   count: redCount,   color: 'var(--red)' },
          ].map(({ label, count, color }) => (
            <div key={label} className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold num" style={{ color }}>
                {count}
              </span>
              <span className="text-sm" style={{ color: 'var(--text-3)' }}>{label}</span>
            </div>
          ))}
          <div className="ml-auto text-xs" style={{ color: 'var(--text-3)' }}>
            {snapshots.length} team{snapshots.length !== 1 ? 's' : ''} tracked
          </div>
        </div>
      )}

      {/* Team grid */}
      {snapshots.length === 0 ? (
        <div
          className="border-2 border-dashed rounded-lg py-20 text-center"
          style={{ borderColor: 'var(--border)' }}
        >
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            No sprint data yet — run the Jira collector to see metrics here
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {snapshots.map((s) => <TeamCard key={s.team.id} snapshot={s} />)}
        </div>
      )}
    </div>
  );
}
